/**
 * Data hooks for a record's documents — §11's presigned pair, wired to one
 * entity_key/record_id at a time (an account, an opportunity, ...).
 */
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useDispatch } from 'react-redux';
import * as filesApi from '../../api/files.api.js';
import { queryKeys } from '../../app/queryClient.js';
import { pushToast } from '../ui/uiSlice.js';

export const MAX_UPLOAD_BYTES = 10 * 1024 * 1024;

/** SHA-256 of a File, hex-encoded — the backend requires it to reserve an upload. */
async function sha256Hex(file) {
  const buffer = await file.arrayBuffer();
  const digest = await crypto.subtle.digest('SHA-256', buffer);
  return Array.from(new Uint8Array(digest)).map((b) => b.toString(16).padStart(2, '0')).join('');
}

export function useAttachments(entityKey, recordId, params = {}) {
  return useQuery({
    queryKey: queryKeys.files.attachments(entityKey, recordId),
    queryFn: () => filesApi.listAttachments({ entity_key: entityKey, record_id: recordId, ...params }),
    enabled: Boolean(entityKey && recordId),
  });
}

/**
 * Upload one file and attach it to (entityKey, recordId) — the three-step
 * dance `docs/API.md` §11 specifies as one mutation, since no caller here
 * wants to see the intermediate states.
 */
export function useUploadDocument(entityKey, recordId) {
  const queryClient = useQueryClient();
  const dispatch = useDispatch();

  return useMutation({
    mutationFn: async (file) => {
      if (file.size > MAX_UPLOAD_BYTES) {
        throw new Error('That file is larger than the 10 MB limit for documents.');
      }
      const checksum = await sha256Hex(file);
      const { object_id: objectId, upload } = await filesApi.createUploadUrl({
        filename: file.name,
        content_type: file.type || 'application/octet-stream',
        byte_size: file.size,
        checksum_sha256: checksum,
        purpose: 'attachment',
        entity_key: entityKey,
        record_id: recordId,
      });
      const putResponse = await filesApi.uploadToPresignedUrl(upload, file);
      const etag = putResponse.headers.etag ?? putResponse.headers.ETag;
      return filesApi.commitUpload(objectId, {
        etag, purpose: 'attachment', entity_key: entityKey, record_id: recordId,
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.files.attachments(entityKey, recordId) });
      dispatch(pushToast({ tone: 'success', message: 'Document uploaded.' }));
    },
    onError: (error) => {
      dispatch(pushToast({ tone: 'danger', message: 'Could not upload that document', detail: error.message }));
    },
  });
}

export function useDownloadDocument() {
  return useMutation({
    mutationFn: (objectId) => filesApi.createDownloadUrl(objectId),
    onSuccess: (result) => {
      // A presigned GET URL, fetched directly by the browser — opening it
      // (rather than an <a download>) is what makes Content-Disposition from
      // the server, not a client-side guess, decide the saved filename.
      window.open(result.url, '_blank', 'noopener');
    },
  });
}

export function useDeleteDocument(entityKey, recordId) {
  const queryClient = useQueryClient();
  const dispatch = useDispatch();

  return useMutation({
    mutationFn: (attachmentId) => filesApi.deleteAttachment(attachmentId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.files.attachments(entityKey, recordId) });
      dispatch(pushToast({ tone: 'success', message: 'Document removed.' }));
    },
    onError: (error) => {
      dispatch(pushToast({ tone: 'danger', message: 'Could not remove that document', detail: error.message }));
    },
  });
}
